<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';

import { BiomarkerExplainerDialog, hasBiomarkerExplanation } from './biomarker-explainer';
import CheckBox from './CheckBox.vue';

interface Option {
  value: any;
  label: string;
}

const props = withDefaults(
  defineProps<{
    label?: string;
    modelValue: any; // string | number | any[]
    options: (string | Option)[];
    multiple?: boolean;
    closeOnContentClick?: boolean;
    maxHeight?: string | number;
    /** Allow the button to shrink and crop label/value with an ellipsis (full text shown in a tooltip). */
    truncate?: boolean;
    /** Allow showing information button for biomarkers that have tutorials/explanations. */
    enableExplainer?: boolean;
  }>(),
  {
    label: '',
    multiple: false,
    closeOnContentClick: undefined,
    maxHeight: undefined,
    truncate: false,
    enableExplainer: true,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void;
  (e: 'change', value: any): void;
}>();
const btnRef = ref<HTMLElement | null>(null);
const isTruncated = ref(false);

const checkTruncation = () => {
  if (!props.truncate || !btnRef.value) {
    isTruncated.value = false;
    return;
  }
  const spans = btnRef.value.querySelectorAll<HTMLElement>(
    '.btn-label, .btn-value',
  );
  isTruncated.value = Array.from(spans).some(
    el => el.scrollWidth > el.clientWidth + 1,
  );
};

const isOpen = ref(false);

const closeOnContentClick = computed(() => {
  if (props.closeOnContentClick !== undefined) {
    return props.closeOnContentClick;
  }
  return !props.multiple;
});

const computedMaxHeight = computed(() => {
  if (props.maxHeight) {
    return typeof props.maxHeight === 'number'
      ? `${props.maxHeight}px`
      : props.maxHeight;
  }
  return 'min(360px, calc(100vh - 120px))';
});

const handleScroll = (event: Event) => {
  if (!isOpen.value) return;
  const target = event.target as Element | null;
  if (
    target
    && typeof target.closest === 'function'
    && target.closest('.glass-menu-list')
  ) {
    return;
  }
  isOpen.value = false;
};

watch(isOpen, (val) => {
  if (val) {
    requestAnimationFrame(() => {
      if (isOpen.value) {
        window.addEventListener('scroll', handleScroll, true);
      }
    });
  }
  else {
    window.removeEventListener('scroll', handleScroll, true);
  }
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll, true);
});

const formattedOptions = computed<Option[]>(() => {
  return props.options.map((opt) => {
    if (typeof opt === 'string') {
      return { value: opt, label: opt };
    }
    return opt;
  });
});

const isSelected = (val: any): boolean => {
  if (props.multiple) {
    return Array.isArray(props.modelValue) && props.modelValue.includes(val);
  }
  return props.modelValue === val;
};

const displayValue = computed(() => {
  if (props.multiple) {
    const selected = Array.isArray(props.modelValue) ? props.modelValue : [];
    if (selected.length === 0) return 'None';
    const labels = selected.map((v) => {
      const opt = formattedOptions.value.find(o => o.value === v);
      return opt ? opt.label : String(v);
    });
    if (labels.length === 1) return labels[0];
    return `${labels[0]} (+${labels.length - 1})`;
  }
  const opt = formattedOptions.value.find(o => o.value === props.modelValue);
  return opt ? opt.label : String(props.modelValue ?? 'None');
});

const selectOption = (val: any) => {
  let nextValue: any;
  if (props.multiple) {
    const current = Array.isArray(props.modelValue)
      ? [...props.modelValue]
      : [];
    if (current.includes(val)) {
      nextValue = current.filter(v => v !== val);
    }
    else {
      nextValue = [...current, val];
    }
  }
  else {
    nextValue = val;
  }
  emit('update:modelValue', nextValue);
  emit('change', nextValue);
};

// Biomarker explanation dialog state
const isExplainerOpen = ref(false);
const explainerBiomarkerId = ref<string>('MYOSI');

const openExplainer = (biomarkerId: string, event?: Event) => {
  if (event) {
    event.stopPropagation();
    event.preventDefault();
  }
  explainerBiomarkerId.value = String(biomarkerId);
  isExplainerOpen.value = true;
};

const currentSelectionHasExplanation = computed(() => {
  if (props.multiple) return false;
  return hasBiomarkerExplanation(props.modelValue);
});
</script>

<template>
  <div class="glass-dropdown" :class="{ truncate }">
    <v-menu
      v-model="isOpen"
      :close-on-content-click="closeOnContentClick"
      :close-on-scroll="false"
      :max-height="computedMaxHeight"
      location="bottom start"
    >
      <template #activator="{ props: activatorProps }">
        <div
          ref="btnRef"
          v-tooltip:bottom="{
            text: `${label ? `${label} ` : ''}${displayValue}`,
            disabled: !isTruncated || isOpen,
          }"
          class="glass-select-btn"
          v-bind="activatorProps"
          @mouseenter="checkTruncation"
        >
          <span v-if="label" class="btn-label">{{ label }}</span>
          <span class="btn-value">{{ displayValue }}</span>
          <button
            v-if="enableExplainer && currentSelectionHasExplanation"
            v-tooltip:top="'Calculation explanation'"
            class="info-icon-btn inline-btn ml-1.5"
            type="button"
            @click.stop="openExplainer(modelValue, $event)"
          >
            <i class="fa-solid fa-circle-info" />
          </button>
          <i class="fa-solid fa-chevron-down caret-icon ml-2" />
        </div>
      </template>
      <v-list class="glass-menu-list">
        <v-list-item
          v-for="opt in formattedOptions"
          :key="String(opt.value)"
          class="glass-menu-item"
          :class="{ active: isSelected(opt.value) }"
          @click="selectOption(opt.value)"
        >
          <div class="flex align-center justify-between gap-2 w-full">
            <div class="flex align-center gap-2 min-w-0">
              <CheckBox v-if="multiple" :checked="isSelected(opt.value)" />
              <v-list-item-title class="item-text">
                {{ opt.label }}
              </v-list-item-title>
            </div>
            <button
              v-if="enableExplainer && hasBiomarkerExplanation(opt.value)"
              v-tooltip:left="'Calculation explanation'"
              class="info-icon-btn menu-btn ml-2"
              type="button"
              @click.stop="openExplainer(opt.value, $event)"
            >
              <i class="fa-solid fa-circle-info" />
            </button>
          </div>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- Biomarker Calculation Explanation Dialog -->
    <BiomarkerExplainerDialog
      v-if="enableExplainer"
      v-model="isExplainerOpen"
      :biomarker-id="explainerBiomarkerId"
      @update:biomarker-id="val => (explainerBiomarkerId = val)"
    />
  </div>
</template>

<style scoped>
.glass-dropdown {
  flex-shrink: 0;
}

.glass-select-btn {
  display: flex;
  align-items: center;
  background: var(--glass-bg-secondary);
  border: 1px solid var(--glass-border);
  border-radius: 8px;
  padding: 2px 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px var(--glass-shadow);
  user-select: none;
  white-space: nowrap;
}

.glass-select-btn:hover {
  background: var(--glass-bg-tertiary);
  border-color: var(--md-outline);
  box-shadow: 0 4px 8px var(--glass-shadow);
}

.btn-label {
  font-size: 11px;
  color: var(--md-text-secondary);
  font-weight: 500;
  margin-right: 4px;
  white-space: nowrap;
  flex-shrink: 0;
}

.btn-value {
  font-size: 11px;
  color: var(--md-text-primary);
  font-weight: 600;
  text-transform: capitalize;
  white-space: nowrap;
  flex-shrink: 0;
}

.caret-icon {
  font-size: 10px;
  color: var(--md-text-secondary);
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

/* Truncation mode: shrink with the container and crop text with ellipsis */
.glass-dropdown.truncate {
  flex-shrink: 1;
  min-width: 0;
}

.glass-dropdown.truncate .glass-select-btn {
  min-width: 0;
  max-width: 100%;
  padding: 2px 10px;
}

.glass-dropdown.truncate .btn-label,
.glass-dropdown.truncate .btn-value {
  flex-shrink: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Crop the label first, then the value */
.glass-dropdown.truncate .btn-label {
  flex-shrink: 3;
  min-width: 1.5em;
}

.glass-dropdown.truncate .btn-value {
  min-width: 2.5em;
}

.glass-select-btn:hover .caret-icon {
  color: var(--md-text-primary);
}

/* Glass menu overrides */
:deep(.glass-menu-list) {
  background-color: var(--md-surface) !important;
  border: 1px solid var(--md-outline) !important;
  border-radius: 8px !important;
  box-shadow: 0 6px 20px var(--glass-shadow) !important;
  padding: 4px 0 !important;
  max-height: min(360px, calc(100vh - 120px)) !important;
  overflow-y: auto !important;
  overscroll-behavior: contain;
}

:deep(.glass-menu-list::-webkit-scrollbar) {
  width: 5px;
}

:deep(.glass-menu-list::-webkit-scrollbar-track) {
  background: transparent;
}

:deep(.glass-menu-list::-webkit-scrollbar-thumb) {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

:deep(.glass-menu-list::-webkit-scrollbar-thumb:hover) {
  background: rgba(255, 255, 255, 0.4);
}

:deep(.glass-menu-item) {
  min-height: 36px !important;
  cursor: pointer !important;
  transition: background-color 0.15s !important;
}

:deep(.glass-menu-item:hover) {
  background-color: var(--md-surface-variant) !important;
}

:deep(.glass-menu-item.active) {
  color: var(--md-primary) !important;
  background-color: var(--md-primary-container) !important;
}

:deep(.item-text) {
  font-size: 12px !important;
}

/* Info Icon Button */
.info-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: var(--md-primary, #00bcd4);
  cursor: pointer;
  padding: 3px;
  border-radius: 50%;
  transition: all 0.15s ease;
  line-height: 1;
}

.info-icon-btn.inline-btn {
  font-size: 11px;
}

.info-icon-btn.menu-btn {
  font-size: 12px;
  opacity: 0.75;
}

.glass-menu-item:hover .info-icon-btn.menu-btn {
  opacity: 1;
}

/* Custom check-box style to support CheckBox component */
:deep(.check-box) {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  border: 1.5px solid var(--md-outline);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
}
:deep(.check-box.checked) {
  background: var(--md-primary);
  border-color: var(--md-primary);
}
:deep(.check-box.checked::after) {
  content: '';
  width: 3px;
  height: 6px;
  border: 1.5px solid var(--md-on-primary);
  border-top: none;
  border-left: none;
  transform: rotate(45deg) translate(-0.5px, -1px);
}
</style>
